import React, { createContext, useContext, useState, useEffect } from 'react';
import { GameThemeConfig } from '../../data/character-config';
import { availableThemes, defaultTheme } from '../../data/game-themes';

// Create context
interface ThemeContextType {
  currentTheme: GameThemeConfig;
  themes: Record<string, GameThemeConfig>;
  setTheme: (themeName: string) => void;
}

const ThemeContext = createContext<ThemeContextType>({
  currentTheme: defaultTheme,
  themes: availableThemes,
  setTheme: () => {},
});

// Create hook for using the theme context
export const useGameTheme = () => useContext(ThemeContext);

// Theme provider component
interface ThemeProviderProps {
  children: React.ReactNode;
}

export const GameThemeProvider: React.FC<ThemeProviderProps> = ({ children }) => {
  const [currentTheme, setCurrentTheme] = useState<GameThemeConfig>(defaultTheme);
  const [isBrowser, setIsBrowser] = useState(false);

  // Only access localStorage in browser
  useEffect(() => {
    setIsBrowser(true);
  }, []);

  useEffect(() => {
    if (isBrowser) {
      const savedTheme = localStorage.getItem('game-theme');
      if (savedTheme && availableThemes[savedTheme]) {
        setCurrentTheme(availableThemes[savedTheme]);
      }
    }
  }, [isBrowser]);

  // Update theme
  const setTheme = (themeName: string) => {
    if (availableThemes[themeName]) {
      setCurrentTheme(availableThemes[themeName]);
      
      if (isBrowser) {
        localStorage.setItem('game-theme', themeName);
      
        // Update CSS variables
        const root = document.documentElement;
        const theme = availableThemes[themeName];
        
        // Apply colors
        Object.entries(theme.colors).forEach(([key, value]) => {
          root.style.setProperty(`--color-${key}`, value);
        });
        
        // Apply fonts
        Object.entries(theme.fonts).forEach(([key, value]) => {
          root.style.setProperty(`--font-${key}`, value);
        });
        
        // Apply elements
        Object.entries(theme.elements).forEach(([key, value]) => {
          root.style.setProperty(`--${key}`, value);
        });
      }
    }
  };

  // Apply theme CSS variables on first render only on client side
  useEffect(() => {
    if (isBrowser) {
      const root = document.documentElement;
      
      // Apply colors
      Object.entries(currentTheme.colors).forEach(([key, value]) => {
        root.style.setProperty(`--color-${key}`, value);
      });
      
      // Apply fonts
      Object.entries(currentTheme.fonts).forEach(([key, value]) => {
        root.style.setProperty(`--font-${key}`, value);
      });
      
      // Apply elements
      Object.entries(currentTheme.elements).forEach(([key, value]) => {
        root.style.setProperty(`--${key}`, value);
      });
    }
  }, [currentTheme, isBrowser]);

  const contextValue = {
    currentTheme,
    themes: availableThemes,
    setTheme,
  };

  return (
    <ThemeContext.Provider value={contextValue}>
      {children}
    </ThemeContext.Provider>
  );
};

export default GameThemeProvider;